/**
 * Safe environment variable access for client-side code
 * Handles cases where process is not defined (Cloudflare Pages Preview, etc.)
 */

interface SafeEnv {
  NEXT_PUBLIC_API_URL?: string;
  NEXT_PUBLIC_API_CORE_URL?: string;
  // server-only variable should not be exposed via getSafeEnv in the client, but typing here helps reuse keys
  DISCORD_WEBHOOK_URL?: string;
}

/**
 * Safely get environment variables on both server and client
 * @returns Environment variables object or empty object if process is not available
 */
export function getSafeEnv(): SafeEnv {
  if (typeof process !== "undefined" && process.env) {
    return {
      NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
      NEXT_PUBLIC_API_CORE_URL: process.env.NEXT_PUBLIC_API_CORE_URL,
      // do not actually use this on client; server route reads process.env directly
      DISCORD_WEBHOOK_URL: undefined,
    };
  }

  // Return empty object when process is not available
  return {};
}

/**
 * Get a specific environment variable safely
 * @param key - The environment variable key
 * @param defaultValue - Default value if not found
 * @returns The environment variable value or default
 */
export function getEnvVar(key: keyof SafeEnv, defaultValue = ""): string {
  const env = getSafeEnv();
  return env[key] || defaultValue;
}
